import canvasActions from './canvasActions'
import Node from './node'
import Link from './link'

function getPointInCanvas (ctx, x, y) {
  const tf = ctx.getTransform().invertSelf()
  return {
    x: tf.a * x + tf.c * y + tf.e,
    y: tf.b * x + tf.d * y + tf.f
  }
}

const maxZoom = 10
const minZoom = 0.1

function zoomAroundPoint (state, pixelX, pixelY, delta) {
  const oldZoom = state.canvasZoom
  const ctx = state.diagramCtx

  let zoom = state.canvasZoom + delta / 200
  if (zoom > maxZoom) zoom = maxZoom
  if (zoom < minZoom) zoom = minZoom
  state.canvasZoom = zoom

  const point = getPointInCanvas(ctx, pixelX, pixelY)

  // keep point unchanged by the zoom by adjusting canvasPos
  state.canvasPos[0] += (zoom - oldZoom) * point.x
  state.canvasPos[1] += (zoom - oldZoom) * point.y
}

// called in onMouseUp and onTouchEnd
function processClick (context, x, y) {
  const state = context.state
  const ctx = state.diagramCtx

  // get click point in canvas-space
  const point = getPointInCanvas(ctx, x, y)

  // trigger onClick for the current canvas action
  state.canvasAction.onClick(context, point.x, point.y)
}

// called in onMouseDown and onTouchStart
function processPress (context, x, y) {
  const state = context.state
  const ctx = state.diagramCtx

  // get press point in canvas-space
  const point = getPointInCanvas(ctx, x, y)

  // search list of nodes to see if any were pressed
  state.canvasAction.onPress(context, point.x, point.y)
}

// called in onMouseMove and onTouchMove
function processDrag (context, dx, dy) {
  const state = context.state

  state.canvasAction.onMove(context, dx, dy)
}

function getCursorStyle (state, x, y) {
  // Get mouse location in canvas-space
  const mouse = getPointInCanvas(state.diagramCtx, x, y)
  let result = ''

  // See if any nodes are hot
  state.nodes.forEach(node => {
    if (node.isInPinBox(mouse.x, mouse.y)) {
      result = 'pointer'
    } else if (node.isIn(mouse.x, mouse.y)) {
      result = 'move'
    }
  })

  // See if any links are hot
  state.links.forEach(link => {
    if (link.isIn(mouse.x, mouse.y)) {
      result = 'pointer'
    }
  })

  return result
}

function applyAction (state, { actionType, action }) {
  if (actionType === 'moveNode') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].pos[0] = action.newx
    state.nodeMap[action.nodeID].pos[1] = action.newy
  } else if (actionType === 'resizeNode') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].size[0] = action.newWidth
    state.nodeMap[action.nodeID].size[1] = action.newHeight
  } else if (actionType === 'changeNodeText') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].text = action.newText
  } else if (actionType === 'changeNodeFill') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].fill = action.newFill
  } else if (actionType === 'changeNodeFont') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].font = action.newFont
  } else if (actionType === 'changeNodeFontColor') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].fontColor = action.newFontColor
  } else if (actionType === 'changeNodeDotColor') {
    if (state.nodeMap[action.nodeID] === undefined) return

    state.nodeMap[action.nodeID].dotColor = action.newDotColor
  } else if (actionType === 'changeNodePin') {

  } else if (actionType === 'deleteNode') {

  } else if (actionType === 'createNode') {

  } else if (actionType === 'createLink') {

  } else if (actionType === 'changeLinkThickness') {
    if (state.linkMap[action.linkID] === undefined) return

    state.linkMap[action.linkID].thickness = action.newThickness
  } else if (actionType === 'changeLinkColor') {
    if (state.linkMap[action.linkID] === undefined) return

    state.linkMap[action.linkID].color = action.newColor
  } else if (actionType === 'deleteLink') {

  }
}

function lerp (a, b, t) {
  return a + (b - a) * t
}

function computeLocalState (state) {
  const epochSec = Date.now() / 1000
  const t = Math.min((epochSec - state.lastBaseStateChange) / state.baseStateBlendTime, 1)

  const startBaseState = state.startBaseState
  const endBaseState = state.endBaseState

  // clear previous local state
  state.nodes = []
  state.nodeMap = {}
  state.links = []
  state.linkMap = {}

  // copy over blended node positions/sizes
  startBaseState.nodes.forEach(node => {
    const startNode = startBaseState.nodeMap[node.id]
    const endNode = endBaseState.nodeMap[node.id]

    const newNode = new Node(
      node.id,
      [lerp(startNode.pos[0], endNode.pos[0], t), lerp(startNode.pos[1], endNode.pos[1], t)],
      [lerp(startNode.size[0], endNode.size[0], t), lerp(startNode.size[1], endNode.size[1], t)],
      node.fill, node.text, node.font, node.fontColor, node.dotColor, node.pins, node.pinnedByMe, node.pinned
    )

    state.nodes.push(newNode)
    state.nodeMap[newNode.id] = newNode
  })

  // copy links
  startBaseState.links.forEach(link => {
    const newLink = new Link(link.id,
      state.nodeMap[link.A.id], state.nodeMap[link.B.id],
      link.thickness, link.color
    )

    state.links.push(newLink)
    state.linkMap[newLink.id] = newLink
  })

  // apply actions
  for (let i = 0; i < state.actions.length; i++) {
    applyAction({
      nodes: state.nodes,
      nodeMap: state.nodeMap,
      links: state.links,
      linkMap: state.linkMap
    }, state.actions[i])
  }
}

function drawArrow (ctx, x, y, rotate) {
  const Th = 9
  const Tw = 14
  const Rh = 21
  const Rw = 3.5

  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(rotate)

  const triangle = new Path2D()
  triangle.moveTo(0, 0)
  triangle.lineTo(+Tw / 2, Th)
  triangle.lineTo(-Tw / 2, Th)
  triangle.closePath()

  const rectangle = new Path2D()
  rectangle.moveTo(-Rw / 2, Th)
  rectangle.lineTo(+Rw / 2, Th)
  rectangle.lineTo(+Rw / 2, Th + Rh)
  rectangle.lineTo(-Rw / 2, Th + Rh)
  rectangle.closePath()

  ctx.fillStyle = 'black'
  ctx.fill(triangle)
  ctx.fill(rectangle)
  ctx.restore()
}

function drawDiagram (state, ctx, canvasPos, canvasZoom) {
  const canvas = ctx.canvas

  // Reset transform
  ctx.setTransform(1, 0, 0, 1, 0, 0)

  // Clear canvas
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Translate to canvas pos
  ctx.translate(-canvasPos[0], -canvasPos[1])

  // Scale to zoom
  ctx.scale(canvasZoom, canvasZoom)

  // Draw grid
  const gridColor = '#badff7'
  const ySpacing = 50
  const xSpacing = 50

  const startY = ySpacing * Math.floor((canvasPos[1] / canvasZoom) / ySpacing)
  for (let i = 0; i < (canvas.height / canvasZoom) / ySpacing + 1; i++) {
    const y = startY + i * ySpacing

    ctx.lineWidth = 2
    ctx.strokeStyle = gridColor
    ctx.beginPath()
    ctx.moveTo(canvasPos[0] / canvasZoom, y)
    ctx.lineTo(canvasPos[0] / canvasZoom + (canvas.width / canvasZoom), y)
    ctx.stroke()
  }

  const startX = xSpacing * Math.floor((canvasPos[0] / canvasZoom) / xSpacing)
  for (let i = 0; i < (canvas.width / canvasZoom) / xSpacing + 1; i++) {
    const x = startX + i * xSpacing

    ctx.lineWidth = 2
    ctx.strokeStyle = gridColor
    ctx.beginPath()
    ctx.moveTo(x, canvasPos[1] / canvasZoom)
    ctx.lineTo(x, canvasPos[1] / canvasZoom + (canvas.height / canvasZoom))
    ctx.stroke()
  }

  // Draw Why/What's stopping arrows
  ctx.save()
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  ctx.fillStyle = 'black'

  ctx.font = '40px arial'
  ctx.fillText('Why?', 5, 40)
  ctx.fillText('What\'s Stopping?', 5, canvas.height - 40)

  drawArrow(ctx, 128, 10, 0)
  drawArrow(ctx, 338, canvas.height - 40, 3.14)

  ctx.font = '20px arial'
  ctx.fillText('Check: Would it help?', 5, 70)
  ctx.fillText('Check: Is it that we have not?', 5, canvas.height - 10)

  ctx.restore()

  // Draw links first
  state.links.forEach(link => {
    link.draw(ctx, state)
  })

  // Draw nodes second
  state.nodes.forEach(node => {
    node.draw(ctx, state)
  })

  // Draw special per-action stuff
  if (state.canvasAction === canvasActions.SelectNodes) {
    const actionData = state.canvasActionData

    if ((actionData.startP !== undefined) && (actionData.endP !== undefined)) {
      ctx.lineWidth = 4
      ctx.strokeStyle = 'black'
      ctx.strokeRect(actionData.startP.x, actionData.startP.y,
        actionData.endP.x - actionData.startP.x,
        actionData.endP.y - actionData.startP.y)
    }
  }

  // Set cursor if in a node/link
  canvas.style.cursor = getCursorStyle(state, state.mousePos[0], state.mousePos[1])
}

function getMaxBounds (state) {
  if (state.nodes.length === 0) {
    return {
      maxX: 0,
      maxY: 0,
      minX: 0,
      minY: 0
    }
  }

  let maxX = -Infinity
  let maxY = -Infinity
  let minX = Infinity
  let minY = Infinity

  state.nodes.forEach((node) => {
    const nmaxX = node.pos[0] + node.size[0]
    const nmaxY = node.pos[1] + node.size[1]
    const nminX = node.pos[0]
    const nminY = node.pos[1]

    if (nmaxX > maxX) { maxX = nmaxX }
    if (nmaxY > maxY) { maxY = nmaxY }
    if (nminX < minX) { minX = nminX }
    if (nminY < minY) { minY = nminY }
  })

  return {
    maxX: maxX,
    maxY: maxY,
    minX: minX,
    minY: minY
  }
}

function drawMinimap (state) {
  const ctx = state.minimapCtx
  const canvas = ctx.canvas

  // Reset transform
  ctx.setTransform(1, 0, 0, 1, 0, 0)

  // Clear canvas
  ctx.fillStyle = 'white'
  ctx.fillRect(0, 0, canvas.width, canvas.height)

  // Get max used bounds of canvas
  const maxBounds = getMaxBounds(state)

  maxBounds.maxX += 40
  maxBounds.maxY += 40
  maxBounds.minX -= 40
  maxBounds.minY -= 40

  // Scale to zoom
  const xsize = maxBounds.maxX - maxBounds.minX
  const ysize = maxBounds.maxY - maxBounds.minY
  const xzoom = canvas.width / xsize
  const yzoom = canvas.height / ysize

  let zoom = 1
  if (xzoom > yzoom) {
    zoom = yzoom
  } else {
    zoom = xzoom
  }
  ctx.scale(zoom, zoom)

  const left = maxBounds.minX - (canvas.width / zoom - xsize) / 2
  const top = maxBounds.minY - (canvas.height / zoom - ysize) / 2
  ctx.translate(-left, -top)

  // Draw links first
  state.links.forEach(link => {
    link.draw(ctx, state)
  })

  // Draw nodes second
  state.nodes.forEach(node => {
    node.draw(ctx, state)
  })

  if (state.nodes.length > 0) {
    // stroke a rectangle on the minimap representing the current diagram view
    const diagramCanvas = state.diagramCtx.canvas
    ctx.lineWidth = 2
    ctx.strokeStyle = 'red'
    ctx.strokeRect(
      state.canvasPos[0] / state.canvasZoom,
      state.canvasPos[1] / state.canvasZoom,
      diagramCanvas.width / state.canvasZoom,
      diagramCanvas.height / state.canvasZoom
    )
  }
}

export default {
  onMouseMove (context, { x, y }) {
    const state = context.state
    const dx = x - state.mousePos[0]
    const dy = y - state.mousePos[1]
    const mouseDown = (state.mouseStart !== null)

    state.mousePos = [x, y]

    if (mouseDown) {
      processDrag(context, dx, dy)
    }
  },
  onMouseDown (context) {
    const state = context.state
    state.mouseStart = state.mousePos

    processPress(context, state.mouseStart[0], state.mouseStart[1])
  },
  onMouseUp (context) {
    const state = context.state

    if (state.mouseStart !== null) {
      const dx = state.mousePos[0] - state.mouseStart[0]
      const dy = state.mousePos[1] - state.mouseStart[1]

      if (dx * dx + dy * dy < 10) {
        // a click occured at state.mouseStart
        processClick(context, state.mouseStart[0], state.mouseStart[1])
      } else {
        console.log('drag')
      }
    }

    state.canvasAction.onRelease(context)
    state.mouseStart = null
  },
  onMouseWheel (context, delta) {
    const state = context.state

    zoomAroundPoint(state, state.mousePos[0], state.mousePos[1], delta)
  },

  onTouchMove (context, touches) {
    const state = context.state

    if (touches.length === 1) {
      if (state.touch0 === null) {
        state.touch0 = touches[0]
      }

      const dx = touches[0].x - state.touch0.x
      const dy = touches[0].y - state.touch0.y

      processDrag(context, dx, dy)

      state.touch0 = touches[0]
      state.touch1 = null
    } else if (touches.length === 2) {
      if ((state.touch0 === null) || (state.touch1 === null)) {
        state.touch0 = touches[0]
        state.touch1 = touches[1]
      }

      const lastDx = state.touch0.x - state.touch1.x
      const lastDy = state.touch0.y - state.touch1.y
      const lastDistance = Math.sqrt(lastDx * lastDx + lastDy * lastDy)

      const dx = touches[0].x - touches[1].x
      const dy = touches[0].y - touches[1].y
      const distance = Math.sqrt(dx * dx + dy * dy)

      const cx = (touches[0].x + touches[1].x) / 2
      const cy = (touches[0].y + touches[1].y) / 2

      const delta = distance - lastDistance
      zoomAroundPoint(state, cx, cy, delta)

      state.touch0 = touches[0]
      state.touch1 = touches[1]
    }
  },
  onTouchStart (context, touches) {
    const state = context.state
    state.touchStart = touches[0]
    state.touch0 = touches[0]

    processPress(context, state.touchStart.x, state.touchStart.y)
  },
  onTouchEnd (context) {
    const state = context.state

    if (state.touchStart !== null) {
      const dx = state.touch0.x - state.touchStart.x
      const dy = state.touch0.y - state.touchStart.y

      if (dx * dx + dy * dy < 10) {
        // a click occured at state.touchStart
        processClick(context, state.touchStart.x, state.touchStart.y)
      } else {
        console.log('drag')
      }
    }

    state.canvasAction.onRelease(context)
    state.touchStart = null
    state.touch0 = null
    state.touch1 = null
  },

  draw (state) {
    computeLocalState(state)

    if ((state.diagramCtx === null) || ((state.minimapCtx === null))) {
      console.log(`NO CANVAS, diagram ${state.diagramCtx}, minimap ${state.minimapCtx}`)
      return
    }

    drawDiagram(state, state.diagramCtx, state.canvasPos, state.canvasZoom)
    drawMinimap(state)
  },

  drawDiagram: drawDiagram,

  getMaxBounds (state) {
    return getMaxBounds(state)
  },
  maxZoom: maxZoom,
  minZoom: minZoom,

  applyAction: applyAction
}
